import React from 'react';
import { motion } from 'framer-motion';
import {
  Monitor, FileText, Package, Users, BarChart3, Truck, Scan, Wifi, Clock,
  ArrowDown, Zap, CreditCard, Receipt, ShieldCheck
} from 'lucide-react';
import { useLanguage } from '@/context/LanguageContext';

const ProblemSection = () => {
  const { t } = useLanguage();

  const tools = [
    { icon: Monitor, label: t('problem_tool_pos') || 'POS System' },
    { icon: Receipt, label: t('problem_tool_invoicing') || 'Invoicing' },
    { icon: Package, label: t('problem_tool_stock') || 'Stock Management' },
    { icon: Users, label: t('problem_tool_staff') || 'Staff Management' },
    { icon: Truck, label: t('problem_tool_delivery') || 'Delivery Tracking' },
    { icon: CreditCard, label: t('problem_tool_payments') || 'Payments' },
    { icon: BarChart3, label: t('problem_tool_reports') || 'Reports' },
    { icon: ShieldCheck, label: t('problem_tool_compliance') || 'Tax Compliance' },
  ];

  return (
    <section className="py-24 md:py-32 bg-[#F8FAFC]" data-testid="problem-section">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center max-w-3xl mx-auto mb-16"
        >
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-[#1e3a5f] tracking-tight mb-6">
            {t('problem_title') || 'Tired of Using Multiple Disconnected Tools?'}
          </h2>
          <p className="text-lg text-slate-600 leading-relaxed">
            {t('problem_subtitle') || 'Most shop owners waste hours every day switching between separate apps for sales, invoicing, stock, and tax compliance. Your POS doesn\'t talk to your invoicing. Sound familiar?'}
          </p>
        </motion.div>

        {/* Tool icons grid */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.2 }}
          className="flex flex-wrap justify-center gap-4 mb-12"
        >
          {tools.map((tool, i) => (
            <div key={i} className="flex items-center gap-2 px-4 py-2.5 bg-white border-2 border-slate-200 text-sm text-slate-600">
              <tool.icon className="w-4 h-4 text-slate-400" />
              {tool.label}
            </div>
          ))}
        </motion.div>

        {/* Arrow down */}
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ delay: 0.4 }}
          className="flex justify-center mb-12"
        >
          <div className="w-12 h-12 bg-[#22c55e] flex items-center justify-center">
            <ArrowDown className="w-6 h-6 text-white" />
          </div>
        </motion.div>

        {/* Solution */}
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true }}
          transition={{ delay: 0.5 }}
          className="text-center"
        >
          <div className="inline-flex items-center gap-3 bg-[#1e3a5f] text-white px-8 py-4 text-lg font-bold shadow-[4px_4px_0px_0px_rgba(34,197,94,0.5)]">
            <Zap className="w-5 h-5 text-[#22c55e]" />
            {t('problem_solution') || 'One Platform. Everything Connected.'}
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default ProblemSection;
